import { useTranslation } from "react-i18next"

import { Camera, Loader2 } from "@/components/icons"
import { Checkbox } from "@/components/ui/checkbox"
import { cn } from "@/lib/utils"

type ScreenshotAttachmentProps = {
  /** null while capture is in progress or after capture failed. */
  previewUrl: string | null
  capturing: boolean
  /** True when the screen shows personal-source content, so no screenshot is taken. */
  suppressed?: boolean
  included: boolean
  onToggle: (include: boolean) => void
}

/**
 * Auto-captured screenshot of the user's screen at dialog-open time.
 * Shown with a checkbox so the user can opt out before submission.
 *
 * Rendered inside the bug and incident steps via the parent dialog's
 * `screenshotSlot` prop.
 */
export function ScreenshotAttachment({
  previewUrl,
  capturing,
  suppressed = false,
  included,
  onToggle,
}: ScreenshotAttachmentProps) {
  const { t } = useTranslation()

  if (suppressed) {
    return (
      <div
        className="border-border bg-muted/10 flex items-center gap-3 rounded-md border p-3 text-xs"
        role="status"
      >
        <Camera aria-hidden className="text-muted-foreground size-4 shrink-0" />
        <span className="text-muted-foreground">
          {t(
            "feedback.screenshot.personalSuppressed",
            "No screenshot is attached: your screen shows content from your personal sources, which is never sent with a report."
          )}
        </span>
      </div>
    )
  }

  if (capturing) {
    return (
      <div className="border-border bg-muted/10 flex items-center gap-3 rounded-md border p-3 text-xs">
        <Loader2 className="text-muted-foreground size-4 animate-spin" />
        <span className="text-muted-foreground">
          {t("feedback.screenshot.capturing", "Capturing a screenshot of your screen…")}
        </span>
      </div>
    )
  }

  if (!previewUrl) {
    // Capture failed silently — skip the UI entirely.
    return null
  }

  const checkboxId = "feedback-include-screenshot"

  return (
    <div className="border-border bg-muted/20 space-y-2 rounded-md border p-3">
      <div className="flex items-start gap-3">
        <img
          alt={t("feedback.screenshot.alt", "Screenshot of your current screen")}
          src={previewUrl}
          className={cn(
            "border-border max-h-24 rounded border object-cover transition-opacity",
            !included && "opacity-40"
          )}
        />
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-sm font-medium">
            {t("feedback.screenshot.title", "Screenshot of your screen")}
          </p>
          <p className="text-muted-foreground text-xs leading-relaxed">
            {t(
              "feedback.screenshot.description",
              "Taken automatically when you opened this dialog so we can see exactly what you were looking at. Uncheck to leave it out."
            )}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 text-xs">
        <Checkbox
          checked={included}
          id={checkboxId}
          onCheckedChange={(checked) => onToggle(checked === true)}
        />
        <label
          className="text-muted-foreground hover:text-foreground flex cursor-pointer items-center gap-2 transition-colors"
          htmlFor={checkboxId}
        >
          <Camera className="size-3.5" />
          {t("feedback.screenshot.includeCheckbox", "Include screenshot with this report")}
        </label>
      </div>
    </div>
  )
}
